import { html, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';
import style from './sector-diversification-table.scss?inline';
import { PopBaseComponent } from '../../base/pop-base-component.js';
import { Constants } from './sector-diversification-table.constants.js';
import type { SectorDiversificationTableContentObject } from './sector-diversification-table.model.js';
import { formatToNDecimalPlaces } from '../../common/utils/formatNumber';
import { sortByNumericProperty } from '../../common/utils/arraySort';

/**
 * @tag pop-sector-diversification-table-component
 * @property {SectorDiversificationTableContentObject} [componentObject] Sector diversification table content
 * @attribute { SectorDiversificationTableContentObject } [data-component-object] SectorDiversificationTable content object
 */
@customElement(Constants.TAG_NAME)
export class PopSectorDiversificationTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * SectorDiversificationTable content object
   */
  componentObject?: SectorDiversificationTableContentObject;

  private renderTableHead() {
    return html`
      <tr>
        <th id="sector-column" scope="col">
          <beacon-table-header variant="normal" sortable="true">
            ${Constants.SECTOR_COLUMN_HEADER}
          </beacon-table-header>
        </th>
        <th id="percent-portfolio-column" scope="col" class="right-align">
          <beacon-table-header variant="normal" sortable="true">
            ${Constants.PERCENT_OF_PORTFOLIO_COLUMN_HEADER}
          </beacon-table-header>
        </th>
        <th id="percent-benchmark-column" scope="col" class="right-align">
          <beacon-table-header variant="normal" sortable="true">
            ${Constants.PERCENT_OF_BENCHMARK_COLUMN_HEADER}
          </beacon-table-header>
        </th>
        <th
          id="percent-underweight-column"
          scope="col"
          class="right-align sorted"
        >
          <beacon-table-header
            variant="normal"
            sortable="true"
            sort-direction="descending"
          >
            ${Constants.PERCENT_OF_UNDERWEIGHT_COLUMN_HEADER}
          </beacon-table-header>
        </th>
      </tr>
    `;
  }

  private renderTableBody() {
    const itemList = sortByNumericProperty(
      this.componentObject?.itemList || [],
      'percentOfUnderWeightOverWeight',
      'desc',
    );

    return html`
      ${itemList?.map((item) => {
        const sectorName = item.sector || Constants.EMPTY_CELL_VALUE;
        const formattedPercentOfFund = formatToNDecimalPlaces(
          item.percentOfFund,
          2,
        );
        const formattedPercentOfIndicativeBenchmark = formatToNDecimalPlaces(
          item.percentOfIndicativeBenchmark,
          2,
        );
        const formattedPercentOfUnderWeightOverWeight = formatToNDecimalPlaces(
          item.percentOfUnderWeightOverWeight,
          2,
        );

        return html`
          <tr>
            <th
              data-beacon-sort-type="string"
              data-beacon-sort-value="${sectorName}"
            >
              <beacon-table-stub>
                <span
                  class="b-typography-sans-serif--body--100 b-font-weight-bold"
                >
                  ${sectorName}
                </span>
              </beacon-table-stub>
            </th>
            <td>
              <beacon-table-record
                variant="numeral"
                sort-value="${formattedPercentOfFund}"
              >
                ${formattedPercentOfFund}
              </beacon-table-record>
            </td>
            <td>
              <beacon-table-record
                variant="numeral"
                sort-value="${formattedPercentOfIndicativeBenchmark}"
              >
                ${formattedPercentOfIndicativeBenchmark}
              </beacon-table-record>
            </td>
            <td>
              <beacon-table-record
                variant="numeral"
                sort-value="${formattedPercentOfUnderWeightOverWeight}"
                sorted="true"
              >
                ${formattedPercentOfUnderWeightOverWeight}
              </beacon-table-record>
            </td>
          </tr>
        `;
      })}
    `;
  }

  render() {
    return this.renderComponent(html`
      <section class="sector-diversification-table-content">
        <beacon-table variant="fixed" sortable="true">
          <table>
            <thead>
              ${this.renderTableHead()}
            </thead>
            <tbody>
              ${this.renderTableBody()}
            </tbody>
          </table>
        </beacon-table>
      </section>
    `);
  }
}
